import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { Table, SearchActions, Button, Badge, ConfirmDialog, unwrapTrpcData } from '@org/ui';
import { trpc, RouterOutputs } from '@org/clients/school';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getMutationErrorMessage } from '../shared/multi-step-utils';

type LearningPathRow = RouterOutputs['learningPaths']['list'][number];

type LearningPathsListProps = {
  onCreatePath: () => void;
  onEditPath: (path: LearningPathRow) => void;
};

export const LearningPathsList: React.FC<LearningPathsListProps> = ({ onCreatePath, onEditPath }) => {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const { data } = trpc.learningPaths.list.useQuery();
  const [filterValue, setFilterValue] = React.useState("");
  const [pendingDelete, setPendingDelete] = React.useState<LearningPathRow | null>(null);
  const [pathData, setPathData] = React.useState<LearningPathRow[]>([]);

  React.useEffect(() => {
    setPathData(unwrapTrpcData<LearningPathRow[]>(data as LearningPathRow[]) ?? []);
  }, [data]);

  const setDefaultMutation = trpc.learningPaths.setDefault.useMutation({
    onSuccess: async () => {
      await utils.learningPaths.list.invalidate();
      toast.success(t('learningPaths.list.toasts.setDefaultSuccess'));
    },
    onError: (error: unknown) =>
      toast.error(t('learningPaths.list.toasts.setDefaultError', { message: getMutationErrorMessage(error) })),
  });

  const deleteMutation = trpc.learningPaths.delete.useMutation({
    onSuccess: async () => {
      await utils.learningPaths.list.invalidate();
      toast.success(t('learningPaths.list.toasts.deleteSuccess'));
    },
    onError: (error: unknown) =>
      toast.error(t('learningPaths.list.toasts.deleteError', { message: getMutationErrorMessage(error) })),
  });

  const handleDelete = (path: LearningPathRow) => {
    setPendingDelete(path);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate({ pathId: pendingDelete.pathId });
    setPendingDelete(null);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
  };

  const columnHelper = createColumnHelper<LearningPathRow>();

  const columns = [
    columnHelper.accessor((row) => row.name || t('learningPaths.list.noName'), {
      id: 'name',
      header: t('learningPaths.list.table.columns.name'),
      cell: (info) => (
        <div className="flex items-center gap-2">
          <span>{info.getValue()}</span>
          {info.row.original.isDefault && (
            <Badge>{t('learningPaths.list.defaultBadge')}</Badge>
          )}
        </div>
      ),
    }),
    columnHelper.accessor((row) => row.description || '', {
      id: 'description',
      header: t('learningPaths.list.table.columns.description'),
    }),
    columnHelper.accessor((row) => (row.isActive ? t('learningPaths.list.activeYes') : t('learningPaths.list.activeNo')), {
      id: 'active',
      header: t('learningPaths.list.table.columns.active'),
      cell: (info) => (
        <Badge variant={info.row.original.isActive ? 'outline' : 'secondary'}>{info.getValue()}</Badge>
      ),
    }),
    columnHelper.accessor((row) => row.journeyCount ?? 0, {
      id: 'journeyCount',
      header: t('learningPaths.list.table.columns.journeys'),
    }),
    columnHelper.display({
      id: 'edit',
      header: t('learningPaths.list.table.columns.actions'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEditPath(row)}
            >
              {t('learningPaths.list.actions.edit')}
            </Button>
            {!row.isDefault && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={setDefaultMutation.isPending || !row.isActive}
                  onClick={(event) => {
                    event.stopPropagation();
                    setDefaultMutation.mutate({ pathId: row.pathId });
                  }}
                >
                  {t('learningPaths.list.actions.makeDefault')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-red-600"
                  disabled={deleteMutation.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDelete(row);
                  }}
                >
                  {t('learningPaths.list.actions.delete')}
                </Button>
              </>
            )}
          </div>
        );
      },
    }),
  ];

  return (
    <div>
      <div><SearchActions
          onClickAdd={onCreatePath}
          onFilterChange={handleFilterChange}
        />
        </div>
      <div><Table
        tableData={pathData as any}
        columns={columns}
        getRowId={(row: any) => row.pathId}
        globalFilter={filterValue}
      /></div>
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        description={t('learningPaths.list.deleteConfirm', { name: pendingDelete?.name ?? '' })}
        confirmLabel={t('learningPaths.list.actions.delete')}
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  )
}
